import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface SocialAccount {
  id: string;
  platform: "instagram" | "youtube" | "tiktok";
  handle: string;
  followers: number;
  posts: number;
}

interface GrowState {
  // Social accounts
  socialAccounts: SocialAccount[];
  selectedAccount: string;
  hasConnectedAccounts: boolean;

  // Actions
  setSocialAccounts: (accounts: SocialAccount[]) => void;
  setSelectedAccount: (accountId: string) => void;
  getSelectedAccountData: () => SocialAccount | undefined;
  resetStore: () => void;
}

const initialState = {
  socialAccounts: [],
  selectedAccount: "",
  hasConnectedAccounts: false,
};

export const useGrowStore = create<GrowState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      getSelectedAccountData: () => {
        const state = get();
        if (!state || !state.socialAccounts) return undefined;
        const { socialAccounts, selectedAccount } = state;
        return socialAccounts.find((acc) => acc.id === selectedAccount);
      },

      // Actions
      setSocialAccounts: (accounts) => {
        set(
          (state) => {
            const hasConnectedAccounts = accounts.length > 0;
            let selectedAccount = state.selectedAccount;

            // Auto-select first account if none is selected and accounts are available
            if (hasConnectedAccounts && !selectedAccount) {
              selectedAccount = accounts[0].id;
            }

            // Reset selected account if it no longer exists in the new accounts
            if (
              selectedAccount &&
              !accounts.find((acc) => acc.id === selectedAccount)
            ) {
              selectedAccount = hasConnectedAccounts ? accounts[0].id : "";
            }

            return {
              socialAccounts: accounts,
              hasConnectedAccounts,
              selectedAccount,
            };
          },
          false,
          "setSocialAccounts",
        );
      },

      setSelectedAccount: (accountId) => {
        set({ selectedAccount: accountId }, false, "setSelectedAccount");
      },

      resetStore: () => {
        set(initialState, false, "resetStore");
      },
    }),
    {
      name: "grow-store-devtools", // Name for devtools
    },
  ),
);
